/**
 * La sessione di verifica per conto di cui il Cockpit lavora.
 *
 * **Questo modulo non e' una macchina a stati nuova.** Apertura, battito,
 * prelease e chiusura sono gia' definiti dal servizio e legati in
 * `reviewSessions.ts` (GI-168); la riconciliazione delle decisioni e' della
 * casella d'uscita (GI-172). Qui c'e' soltanto **l'ordine in cui si fanno**, che
 * e' l'unica cosa che la schermata puo' sbagliare da sola.
 *
 * **Riprendere viene prima di acquisire.** L'apertura e' idempotente per utente,
 * scheda e commessa: chiamarla e' esattamente il gesto di riprendere. Se la
 * sessione ritrovata tiene gia' dei lease, non se ne chiedono altri — la
 * sessione ne puo' tenere cinque, e il sesto sarebbe un rifiuto che l'operatore
 * non sa spiegarsi mentre ha del lavoro in mano che non vede.
 *
 * **Riconciliare viene prima di riprendere lavoro nuovo.** Le decisioni gia'
 * prese possono essere state respinte o rese obsolete dal servizio mentre la
 * pagina era chiusa o la rete giu'. Prendere altri candidati prima di saperlo
 * significa decidere venti volte su uno stato smentito.
 *
 * **Fuori linea non si acquisisce.** Si decide cio' che si tiene gia' in mano.
 * Riservare candidati mentre la rete e' giu' vorrebbe dire tenere fermo del
 * lavoro che nessuno sa di avere in mano.
 *
 * **La chiusura e' esplicita.** Lo smontaggio ferma i timer e non restituisce
 * niente: un ricaricamento per sbaglio non deve rimettere in coda i candidati
 * su cui l'operatore stava ragionando. Il servizio li fa scadere da solo se la
 * scheda non torna.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { ServiceError } from '../api'
import { closeSession, heartbeat, openSession, prelease } from '../reviewSessions'
import type { Lease, ReviewSession } from '../reviewSessions'
import { HEARTBEAT_MS } from '../reviewSessions'
import { forgetReviewSession, rememberReviewSession } from '../reviewSessionMemory'

/**
 * In che stato si trova la sessione, con i nomi che la schermata mostra.
 *
 * `osservazione` non e' un errore: e' il banco aperto da un collegamento
 * incollato, dove guardare il lavoro e' legittimo e deciderlo no.
 */
export type SessionPhase =
  | 'apertura'
  | 'attiva'
  | 'offline'
  | 'osservazione'
  | 'chiusa'
  | 'errore'

export interface ReviewSessionState {
  session: ReviewSession | null
  /** I lease appena presi. Vuoto dopo una ripresa: quelli si avevano gia'. */
  leases: Lease[]
  phase: SessionPhase
  /** Vero quando si e' ritrovato lavoro invece di prenderne di nuovo. */
  resumed: boolean
  degraded: boolean
  online: boolean
  /** Nessun candidato libero: diverso da «coda esaurita». */
  contended: boolean
  /** Vero mentre si chiede al servizio che fine hanno fatto le decisioni. */
  reconciling: boolean
  failure: ServiceError | null
  /**
   * Cresce quando ripresa e acquisizione sono concluse.
   *
   * E' il segnale con cui la schermata rilegge la coda: leggerla prima
   * mostrerebbe una coda senza i candidati appena riservati, cioe' vuota su
   * lavoro che si ha in mano.
   */
  settled: number
  close(): Promise<void>
}

export interface ReviewSessionOptions {
  inspectionId: string
  /** La sessione che arriva dall'indirizzo. `null` significa: si guarda. */
  reviewSessionId?: string | null
  /**
   * La riconciliazione della casella d'uscita (GI-172).
   *
   * Riceve la sessione appena ritrovata e non quella dell'indirizzo: l'apertura
   * e' idempotente e la risposta e' l'autorita' su quale sessione sia davvero
   * di questa scheda. Riconciliare su un identificatore incollato da fuori
   * vorrebbe dire chiedere di decisioni che non sono nostre.
   */
  reconcile?: (reviewSessionId: string) => Promise<void>
}

export function useReviewSession({
  inspectionId,
  reviewSessionId = null,
  reconcile,
}: ReviewSessionOptions): ReviewSessionState {
  const [session, setSession] = useState<ReviewSession | null>(null)
  const [leases, setLeases] = useState<Lease[]>([])
  const [phase, setPhase] = useState<SessionPhase>('apertura')
  const [resumed, setResumed] = useState(false)
  const [degraded, setDegraded] = useState(false)
  const [online, setOnline] = useState<boolean>(() => window.navigator.onLine)
  const [contended, setContended] = useState(false)
  const [reconciling, setReconciling] = useState(false)
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [settled, setSettled] = useState(0)

  // Una sessione chiusa non si rianima da sola: senza questo fermo il ritorno
  // della rete la riaprirebbe, e l'operatore che ha finito il turno si
  // ritroverebbe di nuovo dei candidati in mano.
  const closed = useRef(false)
  const mounted = useRef(true)
  // In un riferimento perche' la funzione di riconciliazione cambia identita' a
  // ogni ridisegno: fra le dipendenze rifarebbe la ripresa da capo, e con essa
  // una seconda acquisizione.
  const reconcileNow = useRef(reconcile)
  reconcileNow.current = reconcile

  /* Si riarma a ogni montaggio, non una volta sola alla costruzione.
   *
   * React in modo severo — ed e' cosi' che il banco gira — monta, smonta e
   * rimonta ogni effetto per dichiarare i difetti di pulizia. Un fermo acceso
   * una volta sola e spento nella pulizia resta spento dopo quel giro: ogni
   * battito riuscito viene buttato, la sessione non si rinfresca piu' e la
   * schermata resta ferma sui valori dell'apertura mentre il lease scade
   * davvero. Il difetto non da' errori: da' una schermata che ha smesso di
   * sapere. */
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  useEffect(() => {
    if (closed.current) return
    let alive = true

    if (reviewSessionId === null) {
      // Nessuna sessione: si guarda il lavoro senza cambiarlo. La coda si legge
      // comunque, perche' una schermata che non chiede niente e' un vicolo
      // cieco.
      setPhase('osservazione')
      setSettled((was) => was + 1)
      return
    }

    if (!online) {
      setPhase('offline')
      setSettled((was) => was + 1)
      return
    }

    setPhase('apertura')
    void (async () => {
      try {
        // Idempotente per utente, scheda e commessa: chiamarla **e'** riprendere.
        const opened = await openSession(inspectionId)
        if (!alive) return
        setSession(opened)
        setFailure(null)
        // Da qui le schede della commessa sanno che il banco ha una sessione:
        // la scheda «Banco» la rimette nell'indirizzo (GI-279).
        rememberReviewSession(inspectionId, opened.review_session_id)

        setReconciling(true)
        try {
          await reconcileNow.current?.(opened.review_session_id)
        } finally {
          if (alive) setReconciling(false)
        }
        if (!alive) return

        if (opened.active_leases > 0) {
          setResumed(true)
          setPhase('attiva')
          setSettled((was) => was + 1)
          return
        }

        setResumed(false)
        const batch = await prelease(opened.review_session_id, opened.max_active_leases)
        if (!alive) return
        setLeases(batch.leases)
        setContended(batch.leases.length === 0)
        setPhase('attiva')
        setSettled((was) => was + 1)
      } catch (error: unknown) {
        if (!alive) return
        setFailure(error instanceof ServiceError ? error : null)
        setPhase('errore')
      }
    })()

    return () => {
      alive = false
    }
  }, [inspectionId, reviewSessionId, online])

  const sessionId = session?.review_session_id ?? null
  // Il ritmo lo dichiara il servizio nella risposta; il valore locale serve solo
  // finche' non c'e' una sessione da cui leggerlo.
  const every = session === null ? HEARTBEAT_MS : session.heartbeat_seconds * 1000

  useEffect(() => {
    if (sessionId === null) return
    if (phase === 'chiusa' || phase === 'errore') return
    // Nessuna chiamata con la rete giu': non terrebbe viva niente e
    // dichiarerebbe «degradata» una situazione che la pagina conosce gia'.
    if (!online) return
    const timer = setInterval(() => {
      heartbeat(sessionId).then(
        (alive) => {
          if (!mounted.current) return
          setSession(alive)
          setDegraded(false)
        },
        () => {
          // Un battito perso non chiude niente: lo dice. La sessione muore
          // quando il servizio smette di sentirla, non quando la pagina lo
          // sospetta.
          if (mounted.current) setDegraded(true)
        },
      )
    }, every)
    return () => clearInterval(timer)
  }, [sessionId, every, phase, online])

  const close = useCallback(async (): Promise<void> => {
    closed.current = true
    setPhase('chiusa')
    // Chiusa per scelta: il banco che si riapre su questa commessa deve
    // essere in sola osservazione, non riprendere di nascosto.
    forgetReviewSession(inspectionId)
    if (sessionId === null) return
    try {
      await closeSession(sessionId)
    } catch {
      // Di qua non c'e' piu' una sessione da usare, e il servizio la fara'
      // scadere da solo. Restare in attesa sarebbe un vicolo cieco proprio nel
      // momento in cui l'operatore vuole andarsene.
    } finally {
      if (mounted.current) {
        setSession(null)
        setLeases([])
        setDegraded(false)
        setContended(false)
      }
    }
  }, [inspectionId, sessionId])

  return {
    session,
    leases,
    phase,
    resumed,
    degraded,
    online,
    contended,
    reconciling,
    failure,
    settled,
    close,
  }
}
